import React, { useState } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Lock, ArrowRight, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If already logged in, redirect to admin
  if (user && user !== false) {
    return <Navigate to={location.state?.from || "/admin"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin");
      navigate(location.state?.from || "/admin");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] grid-bg flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-zinc-950 text-white px-3 py-1 font-mono uppercase text-xs tracking-[0.2em] mb-4">
            <Lightning size={12} weight="fill" /> Admin · Control Room
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">
            Sign in.
          </h1>
          <p className="text-zinc-700 mt-2 font-mono uppercase text-xs tracking-[0.2em]">
            Restricted area
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="border-2 border-zinc-950 bg-white p-6 sm:p-8 space-y-5"
          data-testid="login-form"
        >
          <div>
            <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
              className="rounded-none border-zinc-300 focus-visible:ring-zinc-950 h-11 font-mono text-sm"
              placeholder="admin@example.com"
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
              Password
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="rounded-none border-zinc-300 focus-visible:ring-zinc-950 h-11 font-mono text-sm"
              placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>

          {error && (
            <div
              className="border border-[#FF2A00] bg-red-50 text-[#FF2A00] px-3 py-2 font-mono text-xs uppercase tracking-wider"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider h-12"
          >
            {loading ? (
              "Signing in..."
            ) : (
              <>
                <Lock size={16} weight="bold" className="mr-2" />
                Sign in <ArrowRight size={16} weight="bold" className="ml-2" />
              </>
            )}
          </Button>

          <Link
            to="/"
            className="block text-center font-mono uppercase text-xs tracking-[0.2em] text-zinc-500 hover:text-zinc-950"
          >
            ← Back to shop
          </Link>
        </form>

        <p className="text-center mt-6 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400">
          Protected by JWT · bcrypt
        </p>
      </div>
    </div>
  );
}
